import { type MessageId } from "@cmmd-forge/contracts";
import { useState } from "react";
import type { QueuedComposerMessage } from "../../queuedMessageStore";
import { deriveComposerSendPreflight, deriveComposerSendState } from "../ChatView.logic";
import { newCommandId, newMessageId } from "~/lib/utils";
import { prepareThreadExecutionIntentAction } from "../../lib/runThreadExecutionIntentAction";
import { dispatchChatTurnStart } from "./dispatchChatTurnStart";
import { restoreComposerAfterTurnStartFailure } from "./restoreComposerAfterTurnStartFailure";
import { resolveComposerSubmissionIdentity } from "./composerSubmissionIdentity";
import type {
  ChatTurnSubmissionController,
  ChatTurnSubmissionInput,
} from "./chatTurnSubmissionTypes";
import {
  handleNonSendComposerPreflight,
  reportComposerPersistenceFailure,
} from "./chatTurnSubmissionPreflight";
import { applyOptimisticChatTurn } from "./applyOptimisticChatTurn";
import { releaseUnreferencedComposerFile } from "../../composerFileAttachmentLifecycle";
import { shouldClearStagedSubmissionAfterAcceptedTurnStart } from "./acceptedComposerSubmission";
import { raceWithAbort } from "../../environments/runtime/abortableWait";
import { describeSendFailure } from "../../rpc/transportError";
import { useRuntimeAdmission } from "./useRuntimeAdmission";
import { prepareComposerTurnSend } from "./prepareComposerTurnSend";

/**
 * Sends the Composer's Draft as a turn on the active thread. A send that the Environment does
 * not accept restores the Draft when the Composer is still empty and always reports why.
 */
export function useChatTurnSubmission<ComposerTarget>(
  input: ChatTurnSubmissionInput<ComposerTarget>,
): ChatTurnSubmissionController {
  const admission = useRuntimeAdmission(input.environmentId, input.activeThread?.id);
  // State mirror of sendInFlightRef: a ref clear does not re-render, so the
  // queued-release effect needs the state to retry after a submission ends.
  const [isSendInFlight, setIsSendInFlight] = useState(false);
  /** Submits a draft or claimed queue entry, preserving its snapshot if dispatch fails. */
  // fallow-ignore-next-line complexity -- the send dispatch orchestrator: the busy gate, the queue gate, preflight, staging, dispatch, and the three failure paths are one transaction, so the branches cannot split without hiding the state machine. Refactor candidate for a follow-up ticket.
  const onSend = async (
    e?: { readonly preventDefault: () => void },
    queued?: QueuedComposerMessage,
  ) => {
    e?.preventDefault();
    if (!input.activeThread) return;
    const activeThread = input.activeThread;
    const prepared = prepareComposerTurnSend(input, queued);
    if (!prepared) return;
    const {
      prompt: promptForSend,
      images: composerImages,
      files: composerFilesSnapshot,
      terminalContexts: composerTerminalContexts,
      restoreFailedQueueEntry,
    } = prepared;
    const {
      provider: selectedProvider,
      model: selectedModel,
      models: selectedProviderModels,
      promptEffort: selectedPromptEffort,
      modelSelection: selectedModelSelection,
    } = input.composerModelSelection;
    const {
      trimmedPrompt: trimmed,
      sendableTerminalContexts,
      expiredTerminalContextCount,
    } = deriveComposerSendState({
      prompt: promptForSend,
      imageCount: composerImages.length,
      fileCount: composerFilesSnapshot.length,
      terminalContexts: composerTerminalContexts,
    });
    const preflight = deriveComposerSendPreflight({
      composerDisabledReason: input.composerDisabledReason,
      hasActivePendingProgress: input.hasActivePendingProgress,
      showPlanFollowUpPrompt: input.showPlanFollowUpPrompt,
      hasActiveProposedPlan: Boolean(input.activeProposedPlan),
      trimmedPrompt: trimmed,
      imageCount: composerImages.length,
      fileCount: composerFilesSnapshot.length,
      sendableTerminalContextCount: sendableTerminalContexts.length,
      expiredTerminalContextCount,
      activeProjectExists: Boolean(input.activeProject),
      activeThread: input.activeThread,
      isServerThread: input.isServerThread,
      sendEnvMode: input.sendEnvMode,
      activeThreadBranch: input.activeThreadBranch,
    });
    if (preflight.type !== "send-turn") {
      await handleNonSendComposerPreflight({
        submission: input,
        preflight,
        trimmedPrompt: trimmed,
      }).catch((error: unknown) => {
        restoreFailedQueueEntry(activeThread.id, error);
        throw error;
      });
      restoreFailedQueueEntry(activeThread.id, new Error("preflight rejected the queued send"));
      return;
    }
    if (!input.activeProject) return;
    const activeProject = input.activeProject;
    const threadIdForSend = input.activeThread.id;
    const { baseBranchForWorktree, isFirstMessage, shouldCreateWorktree } = preflight;

    const composerImagesSnapshot = [...composerImages];
    const composerTerminalContextsSnapshot = [...sendableTerminalContexts];
    const priorSubmission = queued
      ? undefined
      : input.readComposerDraft(input.composerDraftTarget)?.stagedSubmission;
    const submissionIdentity = resolveComposerSubmissionIdentity({
      priorSubmission,
      current: {
        prompt: promptForSend,
        attachmentIds: composerImagesSnapshot.map((image) => image.id),
        fileIds: composerFilesSnapshot.map((file) => file.id),
        terminalContextIds: composerTerminalContextsSnapshot.map((context) => context.id),
        modelSelection: selectedModelSelection,
        runtimeMode: input.runtimeMode,
        interactionMode: input.interactionMode,
      },
      now: () => new Date().toISOString(),
      createCommandId: newCommandId,
      createMessageId: newMessageId,
    });
    const commandIdForSend = submissionIdentity.commandId;
    const messageIdForSend = submissionIdentity.messageId;
    const messageCreatedAt = submissionIdentity.startedAt;
    try {
      input.stageComposerSubmission(
        input.composerDraftTarget,
        messageCreatedAt,
        commandIdForSend,
        messageIdForSend,
        submissionIdentity.replaysPriorSubmission ? priorSubmission?.executionIntent : undefined,
        {
          modelSelection: selectedModelSelection,
          runtimeMode: input.runtimeMode,
          interactionMode: input.interactionMode,
        },
      );
    } catch (error) {
      if (
        restoreFailedQueueEntry(
          threadIdForSend,
          error instanceof Error ? error : new Error(String(error)),
        )
      ) {
        return;
      }
      reportComposerPersistenceFailure({
        error,
        threadId: activeThread.id,
        setThreadError: input.setThreadError,
      });
      return;
    }

    input.sendInFlightRef.current = true;
    setIsSendInFlight(true);
    input.beginLocalDispatch({ preparingWorktree: false });
    const preparation = admission.begin(messageCreatedAt);
    input.setThreadError(threadIdForSend, null);
    const outgoingMessageText = await applyOptimisticChatTurn({
      submission: input,
      prompt: promptForSend,
      trimmedPrompt: trimmed,
      images: composerImagesSnapshot,
      files: composerFilesSnapshot,
      terminalContexts: composerTerminalContextsSnapshot,
      expiredTerminalContextCount,
      selectedProvider,
      selectedModel,
      selectedProviderModels,
      selectedPromptEffort,
      messageId: messageIdForSend,
      messageCreatedAt,
    });

    let turnStartAccepted = false;
    await raceWithAbort(
      prepareThreadExecutionIntentAction(input.environmentId, {
        signal: preparation.signal,
        onWaiting: preparation.onWaiting,
        ...(submissionIdentity.replayRuntimeId
          ? { runtimeId: submissionIdentity.replayRuntimeId }
          : {}),
        action: "send",
        projectId: activeProject.cmmdRegistration?.cmmdProjectId ?? null,
        targetId: threadIdForSend,
        dispatchIdentity: commandIdForSend,
      }).then(async (execution) => {
        preparation.signal.throwIfAborted();
        if (execution.intent) {
          input.setStagedComposerExecutionIntent(input.composerDraftTarget, {
            runtimeId: execution.intent.runtimeId,
            intentId: execution.intent.id,
          });
        }
        await execution.dispatch(async (api) => {
          preparation.signal.throwIfAborted();
          await dispatchChatTurnStart({
            signal: preparation.signal,
            onDispatch: () =>
              preparation.useServerInterrupt(async () => {
                await api.orchestration.dispatchCommand({
                  type: "thread.turn.interrupt",
                  commandId: newCommandId(),
                  threadId: threadIdForSend,
                  createdAt: new Date().toISOString(),
                });
              }),
            api,
            environmentId: input.environmentId,
            primaryEnvironmentId: input.primaryEnvironmentId,
            activeProject,
            activeThread,
            draftThread: input.draftThread,
            threadIdForSend,
            messageIdForSend,
            commandIdForSend,
            messageCreatedAt,
            trimmedPrompt: trimmed,
            composerImagesSnapshot,
            composerFilesSnapshot,
            composerTerminalContextsSnapshot,
            outgoingMessageText,
            selectedProvider,
            selectedModel,
            selectedProviderModels,
            selectedPromptEffort,
            selectedModelSelection,
            isFirstMessage,
            isServerThread: input.isServerThread,
            isLocalDraftThread: input.isLocalDraftThread,
            shouldCreateWorktree,
            baseBranchForWorktree,
            activeThreadBranch: input.activeThreadBranch,
            runtimeMode: input.runtimeMode,
            interactionMode: input.interactionMode,
            beginLocalDispatch: input.beginLocalDispatch,
            persistThreadSettingsForNextTurn: input.persistThreadSettingsForNextTurn,
          });
          turnStartAccepted = true;
        });
      }),
      preparation.signal,
    )
      .then(() => {
        if (
          shouldClearStagedSubmissionAfterAcceptedTurnStart(
            submissionIdentity.replaysPriorSubmission,
          )
        ) {
          input.clearStagedComposerSubmission(input.composerDraftTarget);
        }
        if (turnStartAccepted) input.onAcceptedTurnStart?.(threadIdForSend);
        void Promise.all(
          composerFilesSnapshot.map((attachment) => releaseUnreferencedComposerFile(attachment.id)),
        );
      })
      .catch(async (err: unknown) => {
        if (restoreFailedQueueEntry(threadIdForSend, err)) return;
        if (
          !turnStartAccepted &&
          input.promptRef.current.length === 0 &&
          input.composerImagesRef.current.length === 0 &&
          input.composerTerminalContextsRef.current.length === 0
        ) {
          restoreComposerAfterTurnStartFailure({
            messageIdForSend: messageIdForSend as MessageId,
            promptForSend,
            composerImagesSnapshot,
            composerFilesSnapshot,
            composerTerminalContextsSnapshot,
            composerDraftTarget: input.composerDraftTarget,
            readComposerDraft: input.readComposerDraft,
            promptRef: input.promptRef,
            composerImagesRef: input.composerImagesRef,
            composerTerminalContextsRef: input.composerTerminalContextsRef,
            composerRef: input.composerRef,
            setOptimisticUserMessages: input.setOptimisticUserMessages,
            setComposerDraftPrompt: input.setComposerDraftPrompt,
            addComposerDraftImages: input.addComposerDraftImages,
            setComposerDraftFiles: input.setComposerDraftFiles,
            setComposerDraftTerminalContexts: input.setComposerDraftTerminalContexts,
          });
        }
        input.setThreadError(
          threadIdForSend,
          preparation.signal.aborted
            ? "Launch cancelled. Your message is ready to edit or resend."
            : describeSendFailure(err),
        );
      });
    preparation.settle();
    if (!turnStartAccepted) preparation.finish();
    input.sendInFlightRef.current = false;
    setIsSendInFlight(false);
    if (!turnStartAccepted) {
      input.resetLocalDispatch();
    }
  };

  return {
    onSend,
    runtimeAdmission: admission.runtimeAdmission,
    isSendInFlight,
  };
}
